import React, { useEffect, useMemo, useState } from 'react';
import lodash from 'lodash';
import chroma from 'chroma-js';
import { connect } from 'react-redux';
import Loader from '../../../general/Loader';
import TeamPanel from './TeamPanel';
import SettingsForm from '../ui-kit/SettingsForm';
import { Row, Col, Card, ListGroup } from '../../../ui-kit';

import { selectors as combinedScheduleSelectors } from '../../../../state/modules/combined-schedules';
import { selectors as groupSelectors } from '../../../../state/modules/groups';
import { selectors as membershipSelectors } from '../../../../state/modules/memberships';

import DashboardRegistrationManager from '../../DashboardRegistrationManager';

const CrewAvailabilityWidgetSettings = ({ updateWidgetSettingsField, widgetSettings }) => (
  <SettingsForm
    withAutoScroll={true}
    withAutoResizeHeight={true}
    updateWidgetSettingsField={updateWidgetSettingsField}
    widgetSettings={widgetSettings}
  >
    <SettingsForm.Section>
      <SettingsForm.Section.Header />
      <SettingsForm.Section.Content>
        <SettingsForm.ToggleField
          fieldName="show_unavailable_teams"
          label={I18n.t('dashboards.widget_archetypes.available_crew.settings.show_unavailable')}
        />
        <SettingsForm.ToggleField
          fieldName="show_crew_in_unavailable_teams"
          label={I18n.t('dashboards.widget_archetypes.available_crew.settings.show_crew_in_unavailable_teams')}
          disabled={!widgetSettings.show_unavailable_teams}
        />
      </SettingsForm.Section.Content>
    </SettingsForm.Section>
  </SettingsForm>
);

const UnavailableTeamsPanelItem = ({ group }) => {
  const customColorPanelHeader = {
    backgroundColor: chroma(group.color || 'white')
      .alpha(0.25)
      .css()
  };
  return (
    <ListGroup.Item style={customColorPanelHeader}>
      <a href={`/groups/${group.id}/schedule/group_schedule`} style={{ color: 'var(--bs-body-color)' }}>
        {group.name}
      </a>
    </ListGroup.Item>
  );
};
const UnavailableTeamsPanel = ({ groups }) => {
  if (groups.length === 0) return null;
  return (
    <Col>
      <Card border="danger">
        <Card.Header className="opacity-75" style={{ backgroundColor: 'rgba(var(--bs-danger-rgb)' }}>
          <Card.Title>
            <h5>{I18n.t('dashboards.widget_archetypes.available_crew.unavailable_teams')}</h5>
          </Card.Title>
        </Card.Header>
        <ListGroup variant="flush">
          {groups.map(group => (
            <UnavailableTeamsPanelItem key={group.id} group={group} />
          ))}
        </ListGroup>
      </Card>
    </Col>
  );
};

const CrewAvailabilityWidget = ({
  availableMembers,
  dashboardAPIManager,
  groupId,
  groupsById,
  groupsLoaded,
  membershipsLoaded,
  // if true, unavailable teams are displayed
  show_unavailable_teams,
  // if this and show_unavailable_teams is true, the team and its crew will be
  // displayed
  show_crew_in_unavailable_teams,
  taskAvailability,
  teamAvailability,
  width
}) => {
  useEffect(() => {
    dashboardAPIManager.fetchUsers({ params: { presentation_type: 'basic' } });
    dashboardAPIManager.fetchSkills();
    dashboardAPIManager.fetchMemberships();
    dashboardAPIManager.fetchStandbyDutyFunctions();
    dashboardAPIManager.fetchAvailabilityCodes();
  }, []);

  const manager = useMemo(() => new DashboardRegistrationManager(), []);
  const [registered, setRegistered] = useState(false);

  const group = groupsById[groupId];
  const parentGroup = group => groupsById[group.parent_group_id];

  // Move this to a selector / operation
  const subgroupsOrGroup = useMemo(() => {
    if (!group) return [];
    const subgroups = (group.descendant_ids || []).map(descendantId => groupsById[descendantId]);

    // not all subgroups have been loaded yet. Do not return anything, but
    // rather wait until information about subgroups is available. If not,
    // registration to websockets won't happen correctly, and subgroups are
    // regarded as "unavailable". This fixes a race condition causing the
    // crewing widget to be empty.
    // NOTE: If the station's cache is not invalidated when deleting a group
    // The condition below will be true, and render the Widget unusable
    if (subgroups.includes(undefined)) return;

    if (subgroups.length === 0) subgroups.push(group);
    return lodash.sortBy(subgroups, ['display_order']);
  }, [group, groupsById]);

  // show the parent group if the displayed groups have distinct ones
  const shouldShowParentGroup = useMemo(() => {
    if (subgroupsOrGroup === undefined) return false;

    const parents = subgroupsOrGroup.map(group => group.parent_group_id);
    const uniqueParents = lodash.uniq(parents);
    return uniqueParents.length > 1;
  }, [subgroupsOrGroup]);

  useEffect(() => {
    if (registered) return;

    const groupIds = (subgroupsOrGroup || []).map(group => group.id);
    if (groupIds.length === 0) return;

    manager.registerForCombinedScheduleUpdates({ groupIds, type: 'membership' });
    manager.registerForCombinedScheduleUpdates({ groupIds, type: 'team' });
    manager.registerForCombinedScheduleUpdates({ groupIds, type: 'task' });

    setRegistered(true);
  }, [registered, subgroupsOrGroup]);

  if (!groupsLoaded || !membershipsLoaded) return <Loader />;

  if (subgroupsOrGroup === undefined) return <p>{I18n.t('no_results_found')}</p>;

  // make a list of the groups and their availability. If configured, filter
  // out groups that are not currently available (see setting
  // show_unavailable_teams)
  const filteredGroups = subgroupsOrGroup
    .map(group => {
      const { available } = teamAvailability[group.id] || {};

      if (!available) {
        // don't show anything if show_unavailable_teams is false
        if (!show_unavailable_teams) return;
        // don't show if show_unavailable_teams is true, but
        // show_unavailable_teams is false. In that case, the team is shown in
        // the UnavailableTeamsPanel
        if (show_unavailable_teams && !show_crew_in_unavailable_teams) return;
      }

      return { available, group };
    })
    .filter(Boolean);

  // these will be shown in a separate list of unavailable teams, but only if
  // show_unavailable_teams is true, but show_crew_in_unavailable_teams is not.
  // If show_crew_in_unavailable_teams is true, it will show the team as normal
  const unavailableGroups =
    show_unavailable_teams && !show_crew_in_unavailable_teams
      ? subgroupsOrGroup.filter(group => {
          const { available } = teamAvailability[group.id] || {};
          return !available;
        })
      : [];

  let maxCols = { 1: 1, 2: 2, 3: 4, 4: 5, 5: 6, 6: 8 }[width];
  maxCols = Math.min(filteredGroups.length, maxCols);
  if (unavailableGroups.length > 1) maxCols += 1;

  return (
    <Row xs={1} md={Math.min(maxCols, 3)} lg={Math.min(maxCols, 4)} xl={Math.min(maxCols, 6)} className="g-2">
      <>
        <UnavailableTeamsPanel groups={unavailableGroups} />
        {filteredGroups.map(({ group, available }) => (
          <TeamPanel
            key={group.id}
            team={group}
            parentGroup={parentGroup(group)}
            available={available}
            showParentGroup={shouldShowParentGroup}
            availableMembers={availableMembers}
            taskAvailability={taskAvailability}
            groupCount={filteredGroups.length || 0}
          />
        ))}
      </>
    </Row>
  );
};

Object.assign(CrewAvailabilityWidget, {
  SettingsContent: CrewAvailabilityWidgetSettings,
  defaultSettings: {
    show_unavailable_teams: true,
    show_crew_in_unavailable_teams: true
  }
});

const mapStateToProps = state => ({
  groupsById: state.groups.byId,
  groupsLoaded: groupSelectors.initialLoadComplete(state),
  membershipsLoaded: membershipSelectors.initialLoadComplete(state),
  availableMembers: combinedScheduleSelectors.availableMembersAtDashboardTime(state),
  teamAvailability: combinedScheduleSelectors.dashboardTime.team(state),
  taskAvailability: combinedScheduleSelectors.dashboardTime.task(state)
});

export default connect(mapStateToProps)(CrewAvailabilityWidget);
